import { useMemo, useState } from "react"
import { AppLayout } from "@/components/layout/app-layout"
import { useAuth } from "@/lib/auth/auth-context"
import { useDebounce } from "@/lib/utils/use-debounce"
import { useOrganization } from "@/lib/organization/organization-context"
import { useSalesLeads } from "@/lib/api/use-sales-leads"
import {
  salesLeadCustomerName,
  salesLeadVehicleName,
  salesLeadVin,
  salesLeadEstimateValue,
  refId,
  type ApiSalesLead,
  type SalesLeadDepartment,
} from "@/lib/api/types"
import {
  SearchLg as Search,
  Phone,
  Mail01 as Mail,
  CurrencyDollar as DollarSign,
  Star01 as Star,
  User01 as User,
} from "@untitledui/icons"

const DEPT_LABEL: Record<SalesLeadDepartment, string> = { new: "New", used: "Used" }
const DEPT_BADGE: Record<SalesLeadDepartment, string> = {
  new: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200",
  used: "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200",
}

// Sales Rep view — pure list. The BE's `/leads/pending` and `/leads/assigned`
// endpoints both return [] for the SR role today (TBD on BE), so we read the
// dealership-wide `/leads` endpoint (which SRs CAN call) and filter to leads
// where the rep is `me`. When the BE adds SR-aware versions of pending/
// assigned, swap the data source to those.
export function SalesRepView() {
  const { user } = useAuth()
  const { activeDealershipId } = useOrganization()
  const dealershipTeamId = activeDealershipId ? Number(activeDealershipId) : null

  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearch = useDebounce(searchQuery, 300)

  const { data: allLeads = [], isLoading } = useSalesLeads(dealershipTeamId, 1)

  // user.id on the FE is a string (legacy database.ts shape); BE SalesLead.salesRep
  // populates as a numeric User.id. Coerce once and compare.
  const myUserId = user?.id != null ? Number(user.id) : null
  const myLeads = useMemo(() => {
    if (myUserId == null) return []
    return allLeads.filter(lead => refId(lead.salesRep) === myUserId)
  }, [allLeads, myUserId])

  const filtered = useMemo(() => {
    if (debouncedSearch.trim() === "") return myLeads
    const q = debouncedSearch.toLowerCase()
    return myLeads.filter(lead =>
      salesLeadCustomerName(lead).toLowerCase().includes(q) ||
      salesLeadVehicleName(lead).toLowerCase().includes(q) ||
      (salesLeadVin(lead) ?? "").toLowerCase().includes(q) ||
      (lead.email ?? "").toLowerCase().includes(q),
    )
  }, [myLeads, debouncedSearch])

  const totalValue = filtered.reduce((sum, lead) => sum + (salesLeadEstimateValue(lead) ?? 0), 0)

  return (
    <AppLayout>
      <div className="p-4 md:p-8 lg:p-10 max-w-[1600px] mx-auto space-y-6 md:space-y-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900">
            My Total Loss Leads
          </h1>
          <p className="text-zinc-400">
            Customers assigned to you for replacement vehicle follow-up
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
          <RepStatCard label="My Leads" value={String(myLeads.length)} sub="Assigned to you" icon={Star} iconColor="text-blue-500" />
          <RepStatCard
            label="Pipeline Value"
            value={`$${totalValue.toLocaleString()}`}
            sub="Replacement potential"
            icon={DollarSign}
            iconColor="text-emerald-600"
            mono
          />
          <RepStatCard label="Filtered" value={String(filtered.length)} sub="Matching the search" icon={User} iconColor="text-zinc-700" />
        </div>

        <div className="rounded-2xl border border-zinc-200/60 bg-white p-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
            <input
              placeholder="Search by customer, vehicle, VIN, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-4 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150"
            />
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-200/60 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100">
            <h3 className="text-xl font-semibold text-zinc-900">Leads</h3>
          </div>
          <div className="p-5">
            {isLoading ? (
              <div className="text-center py-12 text-zinc-400">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-zinc-400">
                <Star className="size-12 mx-auto mb-4 opacity-50" />
                <p>{myLeads.length === 0 ? "No leads assigned to you yet" : "No leads match your search"}</p>
              </div>
            ) : (
              <RepLeadsTable leads={filtered} />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

function RepStatCard({
  label, value, sub, icon: Icon, iconColor, mono,
}: {
  label: string
  value: string
  sub: string
  icon: React.ComponentType<{ className?: string }>
  iconColor: string
  mono?: boolean
}) {
  return (
    <div className="rounded-2xl border border-zinc-200/60 bg-white p-4 md:p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-zinc-600">{label}</span>
        <Icon className={`size-5 ${iconColor}`} />
      </div>
      <div className={`text-2xl md:text-3xl font-bold text-zinc-900 ${mono ? "font-mono" : ""}`}>{value}</div>
      <p className="text-sm text-zinc-400 mt-1">{sub}</p>
    </div>
  )
}

function RepLeadsTable({ leads }: { leads: ApiSalesLead[] }) {
  const handleCall = (phone: string | null) => {
    if (phone) window.open(`tel:${phone}`)
  }
  const handleEmail = (email: string | null) => {
    if (email) window.open(`mailto:${email}`)
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-100">
            <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Customer</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Vehicle</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Dept.</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Value</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Created</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Contact</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {leads.map((lead) => {
            const customerName = salesLeadCustomerName(lead)
            const vehicleName = salesLeadVehicleName(lead)
            const vin = salesLeadVin(lead)
            const value = salesLeadEstimateValue(lead)
            return (
              <tr key={lead.id} className="hover:bg-zinc-50/50 transition-colors">
                <td className="py-3 px-4">
                  <div>
                    <p className="font-medium text-zinc-900">{customerName}</p>
                    <p className="text-xs text-zinc-400">{lead.phoneNumber || "—"}</p>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <p className="font-medium text-zinc-900">{vehicleName}</p>
                  <p className="text-xs text-zinc-400 font-mono">{vin ?? "—"}</p>
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${DEPT_BADGE[lead.department]}`}>
                    {DEPT_LABEL[lead.department]}
                  </span>
                </td>
                <td className="py-3 px-4 font-medium text-zinc-900 font-mono">
                  {value != null ? `$${value.toLocaleString()}` : "—"}
                </td>
                <td className="py-3 px-4 text-zinc-400">
                  {new Date(lead.createdAt).toLocaleDateString()}
                </td>
                <td className="py-3 px-4">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => handleCall(lead.phoneNumber)}
                      disabled={!lead.phoneNumber}
                      className="size-8 rounded-lg flex items-center justify-center hover:bg-zinc-100 text-zinc-400 hover:text-zinc-900 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Phone className="size-4" />
                    </button>
                    <button
                      onClick={() => handleEmail(lead.email)}
                      disabled={!lead.email}
                      className="size-8 rounded-lg flex items-center justify-center hover:bg-zinc-100 text-zinc-400 hover:text-zinc-900 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Mail className="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
